"use client";
import { CryptoValue } from "@/app/(components)/Basic/CryptoValue";
import { Date as DateComponent } from "@/app/(components)/Basic/Date";
import { Table } from "@/app/(components)/Basic/Table";
import { TableCell } from "@/app/(components)/Basic/TableCell";
import { TableCellContent } from "@/app/(components)/Basic/TableCellContent";
import { TableRow } from "@/app/(components)/Basic/TableRow";
import { UserBasicData } from "@/app/(components)/Basic/UserBasicData";
import { Nft } from "@/api/nfts/types";
import { OfferActionButton } from "../Buttons/OfferAction";
import NoRecordDialog from "@/app/(components)/Basic/NoRecordDialog";
import { useQuery } from "@tanstack/react-query";
import ordersApi from "@/api/orders/client";
import { isExpiredInSeconds } from "@/shared/dates";
import { addDisplayNamePrefix, getUsersName } from "@/api/users/helpers";
import useMe from "@/shared/hooks/useMe";
import { formatDistance } from "date-fns";
import nftsApi from "@/api/nfts/client";
import usersApi from "@/api/users/client";

export interface Props {
  nftId: string;
}

export default function OfferTable({ nftId }: Props) {
  const { data: nft } = useQuery({
    queryKey: ["nft", nftId],
    queryFn: async () => {
      const nftItem = await nftsApi.getSingleNft(nftId);

      const [nft] = await usersApi.enrichWithUser<Nft, "artist">([nftItem], {
        artist: true,
      });

      return nft;
    },
  });

  const { data: offers } = useQuery({
    queryKey: ["offers-for-nft", nft?.id],
    queryFn: async () => {
      if (!nft?.id) {
        return [];
      }

      return await ordersApi.getOffersForNft(nft?.id);
    },
  });

  const { me } = useMe();

  return (
    <Table className="align-middle">
      {offers?.some((offer) => {
        return (
          !isExpiredInSeconds(offer.expiresAt) || offer.buyer === me?.address
        );
      }) ? (
        <>
          <TableRow className="hidden lg:table-row">
            <TableCell>Price</TableCell>
            <TableCell className="lg:w-56">Expiration</TableCell>
            <TableCell className="lg:w-72">From</TableCell>
            <TableCell className="lg:w-40">{""}</TableCell>
          </TableRow>
          {offers.map((offer, index) => {
            const isExpired = isExpiredInSeconds(offer.expiresAt);
            return (
              (!isExpired || offer.buyer === me?.address) && (
                <TableRow key={index}>
                  <TableCell>
                    <TableCellContent label="Price">
                      <CryptoValue
                        cryptoValue={offer.price}
                        paymentErc20TokenAddress={
                          offer.paymentErc20TokenAddress
                        }
                        iconClassName="w-10 h-10"
                        priceClassName="text-body-1"
                      />
                    </TableCellContent>
                  </TableCell>
                  <TableCell className="lg:w-56">
                    <TableCellContent
                      label="Expires"
                      className="flex lg:flex-col items-start gap-y-2"
                    >
                      <div>
                        <DateComponent value={offer.expiresAt * 1000} />
                        {isExpired ? (
                          <label className="text-body-3 font-bold  text-red">
                            EXPIRED
                          </label>
                        ) : (
                          <span className="ml-10">
                            {`(in ${formatDistance(
                              new Date(offer.expiresAt * 1000),
                              new Date(),
                            )})`}
                          </span>
                        )}
                      </div>
                    </TableCellContent>
                  </TableCell>
                  <TableCell className="mb-4 lg:mb-0 lg:w-72">
                    <UserBasicData
                      user={offer.Buyer!}
                      name={getUsersName(offer.Buyer!, true)}
                      image={{ src: offer.Buyer!.imageUrl }}
                      imagePlaceholder={offer.Buyer?.imagePlaceholder}
                      subtitle={addDisplayNamePrefix(offer.Buyer!.displayName)}
                      small
                    />
                  </TableCell>
                  <TableCell className="lg:w-40 text-right">
                    <OfferActionButton nft={nft} offer={offer} />
                  </TableCell>
                </TableRow>
              )
            );
          })}
        </>
      ) : (
        <NoRecordDialog message="No offers for NFT yet" />
      )}
    </Table>
  );
}
